import { useEffect, useRef, useState } from "react";

const PING_KEY = "single_tab_ping";
const PONG_KEY = "single_tab_pong";

const ANSWER_WINDOW = 500;
const RETRY_BLOCKED = 3000;
const REVERIFY_ACTIVE = 10000;

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const write = (key, payload) =>
  localStorage.setItem(
    key,
    JSON.stringify({ ...payload, ts: Date.now(), r: Math.random() })
  );

const read = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
};

export default function useSingleTab() {
  const myId = useRef(newId());
  const status = useRef("checking");
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let stopped = false;
    let answerTimer = null;
    let rivals = [];

    const yieldToOther = () => {
      status.current = "blocked";
      setBlocked(true);
    };

    const takeOver = () => {
      status.current = "active";
      setBlocked(false);
    };

    const ping = () => {
      if (stopped) return;
      rivals = [];
      if (status.current !== "active") status.current = "checking";
      write(PING_KEY, { from: myId.current });
      clearTimeout(answerTimer);
      answerTimer = setTimeout(() => {
        if (stopped) return;
        if (status.current === "blocked") return;
        if (rivals.some((id) => id < myId.current)) yieldToOther();
        else takeOver();
      }, ANSWER_WINDOW);
    };

    const onStorage = (e) => {
      if (stopped || !e.newValue) return;

      if (e.key === PING_KEY) {
        const msg = read(e.newValue);
        if (!msg || msg.from === myId.current) return;
        if (status.current === "active") {
          write(PONG_KEY, { to: msg.from, from: myId.current });
        } else if (status.current === "checking") {
          rivals.push(msg.from);
        }
        return;
      }

      if (e.key === PONG_KEY) {
        const msg = read(e.newValue);
        if (!msg || msg.to !== myId.current) return;
        if (status.current === "active" && msg.from > myId.current) return;
        yieldToOther();
      }
    };

    window.addEventListener("storage", onStorage);
    ping();

    const retry = setInterval(() => {
      if (status.current === "blocked") ping();
    }, RETRY_BLOCKED);

    const reverify = setInterval(() => {
      if (status.current === "active") ping();
    }, REVERIFY_ACTIVE);

    return () => {
      stopped = true;
      clearTimeout(answerTimer);
      clearInterval(retry);
      clearInterval(reverify);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return blocked;
}